/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Button,
  PasswordInput,
  TextInput,
  Toggle
} from 'carbon-components-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { REDACTED_FIELD } from '../../../../ia/util/string';
import { useSpeechInCpd } from '../../../hooks/useSpeechInCpd';
import { ControlledDropdownSelector } from '../speech-common/ControlledDropdownSelector';
import { CustomModels } from './CustomModels';
import { SpeechToText as messages } from './messages';

interface SpeechToTextCpdProps {
  size: string;
  readOnly: boolean;
}

export const SpeechToTextCpd = ({ size, readOnly }: SpeechToTextCpdProps) => {
  const { formatMessage: t } = useIntl();

  const {
    defaultValueModel,
    filtered,
    getSttInstanceUrl,
    handleFetchModels,
    handleSttModelChange,
    instanceId,
    loading,
    models,
    sttModel,
    token,
    updateBearerToken,
    updateInstanceId,
    updateIsFiltered
  } = useSpeechInCpd('STT');

  return (
    <>
      <div className="SpeechToTextCPD__container">
        <TextInput
          id="SpeechToTextCPD__instanceId"
          labelText={t(messages.instanceId)}
          placeholder={t(messages.instanceId)}
          onChange={event => updateInstanceId(event.target.value)}
          onBlur={getSttInstanceUrl}
          value={instanceId}
        />
        <PasswordInput
          id="SpeechToTextCPD__token"
          labelText={t(messages.bearerToken)}
          placeholder={REDACTED_FIELD}
          value={token}
          onChange={event => updateBearerToken(event.target.value)}
        />
        <Button
          className="SpeechToTextCPD__model-button"
          onClick={handleFetchModels}
          disabled={!instanceId || !token || loading}>
          Get Models
        </Button>
      </div>

      {models.length !== 0 && (
        <>
          <div className="SpeechToTextCPD__container">
            <div className="Dropdown__filtered-container">
              <div className="Dropdown__filtered-text">
                <h5>
                  <FormattedMessage {...messages.filterModel} />
                </h5>
                <p className="TabContent__inner-body">
                  <FormattedMessage {...messages.filterWarning} />
                </p>
              </div>
              <div className="Dropdown__filtered-toggle">
                <Toggle
                  id="SpeechToTextCPD__toggle"
                  disabled={models.length === 0}
                  toggled={filtered}
                  onToggle={() => updateIsFiltered(!filtered)}
                />
              </div>
            </div>
          </div>
          <div className="SpeechToTextCPD__container">
            <h5>
              <FormattedMessage {...messages.languageModel} />
            </h5>
            <p className="TabContent__inner-body">
              <FormattedMessage {...messages.languageDescription} />
            </p>
            <div className="Dropdown__filtered-selector">
              <ControlledDropdownSelector
                id="stt-models"
                readOnly={readOnly}
                labelText={t(messages.languageModel)}
                defaultValue={defaultValueModel}
                items={models}
                field={sttModel}
                onChange={handleSttModelChange}
                instanceLoaded={true}
              />
            </div>
          </div>
          {size === 'lg' && <CustomModels />}
        </>
      )}
    </>
  );
};
